/* ============================================
   AROHABLISS SILK HERO — Canvas Animation
   Ported from Next.js silk-background-animation.tsx
   ============================================ */

(function () {
  'use strict';

  /* ---------- HEX COLOUR PARSER ---------- */

  /**
   * Parse a hex colour string like "#B4508C" into {r, g, b}.
   * Falls back to a neutral grey if the value is invalid.
   */
  function hexToRgb(hex) {
    hex = (hex || '').replace('#', '');
    if (hex.length === 3) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    if (hex.length !== 6) return { r: 123, g: 116, b: 129 };
    return {
      r: parseInt(hex.substring(0, 2), 16) || 0,
      g: parseInt(hex.substring(2, 4), 16) || 0,
      b: parseInt(hex.substring(4, 6), 16) || 0
    };
  }

  /* ---------- SILK CANVAS RENDERER ---------- */

  /**
   * Initialise a silk animation on the given <canvas>.
   * @param {HTMLCanvasElement} canvas
   * @param {Object}           opts   Colour / speed overrides
   * @returns {{ destroy: Function }}  Call .destroy() to clean up
   */
  function initSilk(canvas, opts) {
    var ctx = canvas.getContext('2d');
    if (!ctx) return { destroy: function () {} };

    var speed        = opts.speed         || 0.02;
    var scale        = opts.scale         || 2;
    var noiseInt     = opts.noiseIntensity || 0.8;
    var rgb          = hexToRgb(opts.silkColor);
    var colorR       = rgb.r;
    var colorG       = rgb.g;
    var colorB       = rgb.b;
    var gradStart    = opts.gradientStart || '#1a1a1a';
    var gradMid      = opts.gradientMid   || '#2a2a2a';
    var gradEnd      = opts.gradientEnd   || '#1a1a1a';

    var time = 0;
    var rafId = null;

    function resize() {
      canvas.width  = canvas.parentElement.offsetWidth;
      canvas.height = canvas.parentElement.offsetHeight;
    }
    resize();
    window.addEventListener('resize', resize);

    /* Simple deterministic noise */
    var G = 2.71828;
    function noise(x, y) {
      var rx = G * Math.sin(G * x);
      var ry = G * Math.sin(G * y);
      return (rx * ry * (1 + x)) % 1;
    }

    function animate() {
      var w = canvas.width;
      var h = canvas.height;

      /* Background gradient fill */
      var grad = ctx.createLinearGradient(0, 0, w, h);
      grad.addColorStop(0,   gradStart);
      grad.addColorStop(0.5, gradMid);
      grad.addColorStop(1,   gradEnd);
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);

      /* Silk pattern — pixel manipulation */
      var img  = ctx.createImageData(w, h);
      var data = img.data;

      for (var x = 0; x < w; x += 2) {
        for (var y = 0; y < h; y += 2) {
          var u = (x / w) * scale;
          var v = (y / h) * scale;
          var tOff = speed * time;
          var tx = u;
          var ty = v + 0.03 * Math.sin(8.0 * tx - tOff);

          var pattern =
            0.6 +
            0.4 *
              Math.sin(
                5.0 * (tx + ty + Math.cos(3.0 * tx + 5.0 * ty) + 0.02 * tOff) +
                Math.sin(20.0 * (tx + ty - 0.1 * tOff))
              );

          var rnd = noise(x, y);
          var intensity = Math.max(0, pattern - (rnd / 15.0) * noiseInt);

          var idx = (y * w + x) * 4;
          if (idx < data.length) {
            data[idx]     = Math.floor(colorR * intensity);
            data[idx + 1] = Math.floor(colorG * intensity);
            data[idx + 2] = Math.floor(colorB * intensity);
            data[idx + 3] = 255;
          }
        }
      }
      ctx.putImageData(img, 0, 0);

      /* Radial vignette for depth */
      var vig = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, Math.max(w, h) / 2);
      vig.addColorStop(0, 'rgba(0,0,0,0.1)');
      vig.addColorStop(1, 'rgba(0,0,0,0.4)');
      ctx.fillStyle = vig;
      ctx.fillRect(0, 0, w, h);

      time += 1;
      rafId = requestAnimationFrame(animate);
    }

    animate();

    return {
      destroy: function () {
        window.removeEventListener('resize', resize);
        if (rafId) cancelAnimationFrame(rafId);
      }
    };
  }

  /* ---------- SLIDESHOW CONTROLLER ---------- */

  /**
   * Manages multiple silk-hero slides with auto-rotation and transitions.
   */
  function SilkHeroController(section) {
    var slides       = section.querySelectorAll('.silk-hero__slide');
    var dots         = section.querySelectorAll('.silk-hero__dot');
    var prevBtn      = section.querySelector('.silk-hero__arrow--prev');
    var nextBtn      = section.querySelector('.silk-hero__arrow--next');
    var current      = 0;
    var total        = slides.length;
    var transitioning = false;
    var timer        = null;
    var canvases     = [];
    var autoSpeed    = parseInt(section.dataset.autoSpeed || '7000', 10);

    /* Initialise each canvas with per-slide colour settings */
    slides.forEach(function (slide) {
      var canvas = slide.querySelector('.silk-hero__canvas');
      if (!canvas) return;
      var inst = initSilk(canvas, {
        speed:          parseFloat(slide.dataset.silkSpeed        || '0.02'),
        scale:          parseFloat(slide.dataset.silkScale        || '2'),
        noiseIntensity: parseFloat(slide.dataset.silkNoise        || '0.8'),
        silkColor:      slide.dataset.silkColor                   || '#7B7481',
        gradientStart:  slide.dataset.silkGradStart               || '#1a1a1a',
        gradientMid:    slide.dataset.silkGradMid                 || '#2a2a2a',
        gradientEnd:    slide.dataset.silkGradEnd                 || '#1a1a1a',
      });
      canvases.push(inst);
    });

    function goTo(index) {
      if (transitioning || index === current) return;
      transitioning = true;

      slides[current].classList.remove('silk-hero__slide--active');
      dots[current] && dots[current].classList.remove('silk-hero__dot--active');

      current = (index + total) % total;

      slides[current].classList.add('silk-hero__slide--active');
      dots[current] && dots[current].classList.add('silk-hero__dot--active');

      /* Re-trigger content animation */
      var content = slides[current].querySelector('.silk-hero__content');
      if (content) {
        content.classList.remove('silk-hero__content--visible');
        void content.offsetWidth; /* force reflow */
        content.classList.add('silk-hero__content--visible');
      }

      setTimeout(function () { transitioning = false; }, 700);
    }

    function next() { goTo(current + 1); }
    function prev() { goTo(current - 1); }

    function startAutoplay() {
      stopAutoplay();
      timer = setInterval(next, autoSpeed);
    }
    function stopAutoplay() {
      if (timer) { clearInterval(timer); timer = null; }
    }

    /* Navigation events */
    if (prevBtn) prevBtn.addEventListener('click', function () { prev(); startAutoplay(); });
    if (nextBtn) nextBtn.addEventListener('click', function () { next(); startAutoplay(); });
    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () { goTo(i); startAutoplay(); });
    });

    /* Pause on hover (desktop only) */
    section.addEventListener('mouseenter', stopAutoplay);
    section.addEventListener('mouseleave', startAutoplay);

    /* Initial state */
    if (slides[0]) {
      slides[0].classList.add('silk-hero__slide--active');
      var firstContent = slides[0].querySelector('.silk-hero__content');
      if (firstContent) firstContent.classList.add('silk-hero__content--visible');
    }
    if (dots[0]) dots[0].classList.add('silk-hero__dot--active');

    startAutoplay();

    return {
      destroy: function () {
        stopAutoplay();
        canvases.forEach(function (c) { c.destroy(); });
      }
    };
  }

  /* ---------- BOOT ---------- */

  function boot() {
    document.querySelectorAll('.silk-hero').forEach(function (section) {
      SilkHeroController(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Shopify Theme Editor live-reload support */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) {
      var section = e.target.querySelector('.silk-hero');
      if (section) SilkHeroController(section);
    });
  }
})();
